import { useMutation } from "@tanstack/react-query";
import { dfetch } from "src/utils/dfetch.ts";
import { notify } from "@utils/notification.ts";

export interface TestControlPlaneResult {
  success: boolean;
  message: string;
}

/**
 * POST `/api/system-settings/test-control-plane` — tests the saved Control Plane
 * Connection settings. A failed check still answers 200, with `success: false` and
 * what to fix, so the toast shows the server's reason (green or red). Only a request
 * that did not run at all goes through `dfetch`'s generic error toast.
 */
export const useTestControlPlaneMutation = () =>
  useMutation({
    mutationFn: async (): Promise<TestControlPlaneResult> =>
      (
        await dfetch("/api/system-settings/test-control-plane", undefined, "POST")
      ).json(),
    onSuccess: ({ success, message }) => {
      notify(
        message,
        success ? { variant: "success" } : { variant: "error", skipAnalytics: true }
      );
    },
  });

/**
 * POST `/api/system-settings/test-integration/{type}` — tests one integration entry
 * as typed in its dialog, before it is saved. Same contract as the Control Plane
 * test: a failed check answers 200 with `success: false` and what to fix.
 */
export const useTestIntegrationMutation = () =>
  useMutation({
    mutationFn: async ({
      type,
      entry,
    }: {
      type: string;
      entry: unknown;
    }): Promise<TestControlPlaneResult> =>
      (
        await dfetch(`/api/system-settings/test-integration/${type}`, entry, "POST")
      ).json(),
    onSuccess: ({ success, message }) => {
      notify(
        message,
        success ? { variant: "success" } : { variant: "error", skipAnalytics: true }
      );
    },
  });
